import {
  NavLink,
  NavLinkProps,
  useLocation,
  useMatch,
  useNavigate,
  useResolvedPath,
} from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'src/stores/auth'
import { useLogout, useUser } from 'src/services/user'
import { useSidebar } from 'src/stores/sidebar'
import { usePermissions } from 'src/hooks/use-permissions'
import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react'
import {
  FaHome,
  FaDollarSign,
  FaFileAlt,
  FaReceipt,
  FaComments,
  FaSignOutAlt,
  FaCog,
  FaBuilding,
  FaFileInvoiceDollar,
  FaUsers,
  FaWallet,
} from 'react-icons/fa'
import {
  BanknotesIcon,
  BellIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  Cog6ToothIcon,
  HomeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { WHATSAPP_TEMPLATES_MANAGE_PERMISSION } from 'src/services/whatsapp'
import { useQuery } from '@tanstack/react-query'
import { Button } from './button'
import { LanguageSwitcher } from './language-switcher'
import { SegmentedControl } from './segmented-control'
import { cn } from 'src/utils/style'
import { notificationQueries } from 'src/services/notifications'
import { NotificationList } from 'src/components/notifications/notification-list'
import { ActiveTasksList } from 'src/components/layout/active-tasks-list'
import { WithTooltip } from 'src/components/tooltip'
import { useActiveJobCount, useUnacknowledgedFailureCount } from 'src/stores/async-jobs'

// Caps a numeric count for an icon badge so it never overflows the glyph.
const formatBadgeCount = (count: number) => (count > 9 ? '9+' : String(count))

// Live unread-notification count, shared by the desktop sidebar entry and the
// mobile bottom bar so both show the same number.
const useUnreadNotificationCount = () => {
  const token = useAuth.use.token()
  const { data } = useQuery({
    ...notificationQueries.unreadCount(),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    enabled: Boolean(token),
  })
  return data?.count ?? 0
}

// --- Shared sub-components ---

interface SidebarLinkProps extends NavLinkProps {
  isCollapsed?: boolean
  title?: string
}

// Same match rule as NavLink's default (prefix match, `end` off). Resolved here
// because the tooltip trigger (Radix Slot, `asChild`) stringifies a function
// `className`/`children`, so links inside `WithTooltip` must get plain values.
const useIsNavActive = (to: NavLinkProps['to']) => {
  const { pathname } = useResolvedPath(to)
  return Boolean(useMatch({ path: pathname, end: false }))
}

const SidebarLink = ({ to, children, isCollapsed, title }: PropsWithChildren<SidebarLinkProps>) => {
  const isActive = useIsNavActive(to)
  return (
    <WithTooltip label={title} side="right" enabled={isCollapsed}>
      <NavLink
        to={to}
        aria-label={title}
        className={`group relative flex flex-col items-center justify-center p-3 text-xs transition-all duration-200 sm:flex-row sm:justify-start sm:space-x-3 sm:rounded-lg sm:text-base ${
          isCollapsed ? 'sm:h-12 sm:w-12 sm:flex-col' : 'sm:w-full'
        } ${
          isActive
            ? 'bg-primary-100 text-primary'
            : 'text-gray-600 hover:bg-surface-hover hover:text-gray-900'
        }`}
      >
        {children}
      </NavLink>
    </WithTooltip>
  )
}

interface SidebarButtonProps {
  onClick: () => void
  isCollapsed?: boolean
  title?: string
}

const SidebarButton = ({
  onClick,
  children,
  isCollapsed,
  title,
}: PropsWithChildren<SidebarButtonProps>) => {
  return (
    <Button
      onClick={onClick}
      aria-label={title}
      tooltip={isCollapsed ? title : undefined}
      variant="ghost"
      className={`group relative flex flex-col items-center justify-center text-xs transition-all duration-200 sm:flex-row sm:justify-start sm:space-x-3 sm:text-base ${
        isCollapsed ? 'sm:h-12 sm:w-12 sm:flex-col' : 'sm:w-full'
      }`}
    >
      {children}
    </Button>
  )
}

// --- Desktop-only: flyout entry (notifications / alerts) ---
//
// A sidebar item styled like SidebarLink but, instead of navigating, it reveals
// a flyout panel anchored to the entry. The flyout works in both the collapsed
// and expanded states so the count badge stays at-a-glance either way.

interface SidebarFlyoutEntryProps {
  icon: ReactNode
  label: string
  count: number
  isCollapsed?: boolean
  badgeAriaLabel?: string
  testId?: string
  /** Distinct indicator shown when something needs attention (e.g. failed tasks). */
  showFailureIndicator?: boolean
  failureIndicatorAriaLabel?: string
  children: (close: () => void) => ReactNode
}

// Icon + label + count badge shown inside a flyout entry's Button. Kept as its
// own component so the Button's child is a single expression (the button-label
// i18n guard treats nested JSX literals here — classNames — as product copy).
const FlyoutEntryContent = ({
  icon,
  label,
  count,
  isCollapsed,
  badgeAriaLabel,
  badgeTestId,
  showFailureIndicator,
  failureIndicatorAriaLabel,
  failureTestId,
}: {
  icon: ReactNode
  label: string
  count: number
  isCollapsed?: boolean
  badgeAriaLabel?: string
  badgeTestId?: string
  showFailureIndicator?: boolean
  failureIndicatorAriaLabel?: string
  failureTestId?: string
}) => {
  const hasCount = count > 0
  return (
    <>
      <span className="relative flex items-center justify-center">
        {icon}
        {hasCount && isCollapsed ? (
          <span
            aria-label={badgeAriaLabel}
            data-testid={badgeTestId}
            className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold leading-none text-white"
          >
            {formatBadgeCount(count)}
          </span>
        ) : null}
        {showFailureIndicator && isCollapsed ? (
          <ExclamationTriangleIcon
            aria-label={failureIndicatorAriaLabel}
            data-testid={failureTestId}
            className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 text-error-500"
          />
        ) : null}
      </span>
      <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>{label}</span>
      {!isCollapsed && (hasCount || showFailureIndicator) ? (
        <span className="ml-auto hidden items-center gap-1.5 sm:flex">
          {showFailureIndicator ? (
            <ExclamationTriangleIcon
              aria-label={failureIndicatorAriaLabel}
              data-testid={failureTestId}
              className="h-4 w-4 text-error-500"
            />
          ) : null}
          {hasCount ? (
            <span
              aria-label={badgeAriaLabel}
              data-testid={badgeTestId}
              className="flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1.5 text-[10px] font-bold leading-none text-white"
            >
              {formatBadgeCount(count)}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  )
}

const SidebarFlyoutEntry = ({
  icon,
  label,
  count,
  isCollapsed,
  badgeAriaLabel,
  testId,
  showFailureIndicator,
  failureIndicatorAriaLabel,
  children,
}: SidebarFlyoutEntryProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="ghost"
        tone="neutral"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={label}
        tooltip={isCollapsed && !isOpen ? label : undefined}
        data-testid={testId}
        className={`group relative flex flex-col items-center justify-center p-3 text-xs transition-all duration-200 sm:flex-row sm:justify-start sm:space-x-3 sm:rounded-lg sm:text-base ${
          isCollapsed ? 'sm:h-12 sm:w-12 sm:flex-col' : 'sm:w-full'
        } ${isOpen ? 'bg-primary-100 text-primary' : 'text-gray-600 hover:text-gray-900'}`}
      >
        <FlyoutEntryContent
          icon={icon}
          label={label}
          count={count}
          isCollapsed={isCollapsed}
          badgeAriaLabel={badgeAriaLabel}
          badgeTestId={testId ? `${testId}-badge` : undefined}
          showFailureIndicator={showFailureIndicator}
          failureIndicatorAriaLabel={failureIndicatorAriaLabel}
          failureTestId={testId ? `${testId}-failed` : undefined}
        />
      </Button>

      {isOpen && (
        <div className="absolute bottom-0 left-full z-dropdown ml-2 w-80 sm:w-96">
          {children(() => setIsOpen(false))}
        </div>
      )}
    </div>
  )
}

// --- Notifications side-nav entry ---

const NotificationsNavEntry = ({ isCollapsed }: { isCollapsed?: boolean }) => {
  const { t } = useTranslation()
  const unreadCount = useUnreadNotificationCount()

  return (
    <SidebarFlyoutEntry
      icon={<BellIcon className="h-6 w-6" aria-hidden="true" />}
      label={t('notifications.title')}
      count={unreadCount}
      isCollapsed={isCollapsed}
      badgeAriaLabel={t('notifications.unreadCount', { count: unreadCount })}
      testId="nav-notifications"
    >
      {(close) => <NotificationList onClose={close} />}
    </SidebarFlyoutEntry>
  )
}

// --- Desktop-only: collapsible group ---

interface SidebarGroupProps {
  label: string
  isCollapsed?: boolean
  children: ReactNode
}

const SidebarGroup = ({ label, isCollapsed, children }: SidebarGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <div>
      {!isCollapsed && (
        <Button
          variant="ghost"
          tone="neutral"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wider"
        >
          <span>{label}</span>
          <ChevronDownIcon
            className={`h-3 w-3 transition-transform duration-200 ${
              isExpanded ? '' : '-rotate-90'
            }`}
          />
        </Button>
      )}
      {(isCollapsed || isExpanded) && <div className="flex flex-col">{children}</div>}
    </div>
  )
}

// --- Mobile-only: bottom bar sub-item link ---

interface MobileNavItem {
  to: string
  label: string
  icon: ReactNode
}

type MobileSection = 'hosting' | 'accounting' | 'settings'

interface MobileNavGroup {
  key: MobileSection
  label: string
  icon: ReactNode
  items: MobileNavItem[]
}

const visibleItems = (items: (MobileNavItem | false)[]) =>
  items.filter((item): item is MobileNavItem => Boolean(item))

// Icon-only sub-item; the active one also shows its label so the current page
// stays readable without crowding the bar.
const MobileChildLink = ({ to, label, icon }: MobileNavItem) => {
  const isActive = useIsNavActive(to)
  return (
    <WithTooltip label={label} side="top">
      <NavLink
        to={to}
        aria-label={label}
        className={cn([
          'relative flex min-w-12 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-3 text-sm transition-colors',
          isActive ? 'font-semibold text-primary' : 'text-gray-600 hover:text-gray-900',
        ])}
      >
        {isActive && (
          <span
            aria-hidden="true"
            className="absolute inset-x-3 top-0 h-0.5 rounded-b bg-primary"
          />
        )}
        {icon}
        <span className={isActive ? undefined : 'sr-only'}>{label}</span>
      </NavLink>
    </WithTooltip>
  )
}

// --- Mobile-only: bottom-bar Inbox content ---

// Icon + count badge + label for the mobile Inbox button. Separate component so
// the Button's child is a single expression (keeps the button-label i18n guard
// from reading nested classNames as product copy).
const MobileFlyoutButtonContent = ({
  icon,
  label,
  count,
  badgeAriaLabel,
  showFailureIndicator,
  failureIndicatorAriaLabel,
  badgeTestId,
  failureTestId,
}: {
  icon: ReactNode
  label: string
  count: number
  badgeAriaLabel?: string
  showFailureIndicator?: boolean
  failureIndicatorAriaLabel?: string
  badgeTestId?: string
  failureTestId?: string
}) => {
  const hasCount = count > 0
  return (
    <>
      <span className="relative flex items-center justify-center">
        {icon}
        {hasCount ? (
          <span
            aria-label={badgeAriaLabel}
            data-testid={badgeTestId}
            className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold leading-none text-white"
          >
            {formatBadgeCount(count)}
          </span>
        ) : null}
        {showFailureIndicator ? (
          <ExclamationTriangleIcon
            aria-label={failureIndicatorAriaLabel}
            data-testid={failureTestId}
            className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 text-error-500"
          />
        ) : null}
      </span>
      <span className="sr-only">{label}</span>
    </>
  )
}

// --- Mobile bottom bar ---
//
// The bar shows one group's sub-items at a time, with the group itself pinned
// on the left as a switcher. Tapping the switcher swaps the bar to the group
// list; picking a group swaps its sub-items back in. Inbox (notifications +
// background tasks) stays pinned on the right.

const MobileBottomBar = ({ onLogout }: { onLogout: () => void }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { hasPermissionInAnyOrg } = usePermissions()
  const barRef = useRef<HTMLDivElement>(null)

  const hasExpenses = hasPermissionInAnyOrg('expenses.access')
  const hasReservations = hasPermissionInAnyOrg('reservations.access')
  const hasWhatsapp =
    hasPermissionInAnyOrg('chat.access') ||
    hasPermissionInAnyOrg(WHATSAPP_TEMPLATES_MANAGE_PERMISSION)
  const hasProperties = hasPermissionInAnyOrg('properties.access')
  const hasReports = hasPermissionInAnyOrg('reports.access')
  const hasReceipts = hasPermissionInAnyOrg('receipts.access')
  const hasStatements = hasPermissionInAnyOrg('statements.access')
  const hasVendors = hasPermissionInAnyOrg('vendors.access')

  const unreadCount = useUnreadNotificationCount()
  const activeCount = useActiveJobCount()
  const failureCount = useUnacknowledgedFailureCount()

  const groups: MobileNavGroup[] = [
    {
      key: 'hosting' as const,
      label: t('sidebar.hosting'),
      icon: <HomeIcon className="h-5 w-5" aria-hidden="true" />,
      items: visibleItems([
        hasReservations && {
          to: '/reservations',
          label: t('sidebar.reservations'),
          icon: <FaHome className="h-5 w-5" aria-hidden="true" />,
        },
        hasProperties && {
          to: '/properties',
          label: t('sidebar.properties'),
          icon: <FaBuilding className="h-5 w-5" aria-hidden="true" />,
        },
        hasWhatsapp && {
          to: '/chat-clients',
          label: t('sidebar.whatsapp'),
          icon: <FaComments className="h-5 w-5" aria-hidden="true" />,
        },
      ]),
    },
    {
      key: 'accounting' as const,
      label: t('sidebar.accounting'),
      icon: <BanknotesIcon className="h-5 w-5" aria-hidden="true" />,
      items: visibleItems([
        hasExpenses && {
          to: '/expenses',
          label: t('sidebar.expenses'),
          icon: <FaDollarSign className="h-5 w-5" aria-hidden="true" />,
        },
        hasReports && {
          to: '/expense-reports',
          label: t('sidebar.reports'),
          icon: <FaFileAlt className="h-5 w-5" aria-hidden="true" />,
        },
        hasReceipts && {
          to: '/receipts',
          label: t('sidebar.receipts'),
          icon: <FaReceipt className="h-5 w-5" aria-hidden="true" />,
        },
        hasExpenses && {
          to: '/payment-accounts',
          label: t('sidebar.paymentAccounts'),
          icon: <FaWallet className="h-5 w-5" aria-hidden="true" />,
        },
        hasStatements && {
          to: '/statements',
          label: t('sidebar.statements'),
          icon: <FaFileInvoiceDollar className="h-5 w-5" aria-hidden="true" />,
        },
        hasVendors && {
          to: '/vendors',
          label: t('sidebar.vendors'),
          icon: <FaUsers className="h-5 w-5" aria-hidden="true" />,
        },
      ]),
    },
    {
      key: 'settings' as const,
      label: t('sidebar.settings'),
      icon: <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />,
      items: [
        {
          to: '/organization-settings',
          label: t('sidebar.settings'),
          icon: <FaCog className="h-5 w-5" aria-hidden="true" />,
        },
      ],
    },
  ].filter((group) => group.items.length > 0)

  const routeSection = groups.find((group) =>
    group.items.some((item) => location.pathname.startsWith(item.to)),
  )?.key

  const [section, setSection] = useState<MobileSection>(routeSection ?? groups[0].key)
  const [isSwitching, setIsSwitching] = useState(false)
  const [isInboxOpen, setIsInboxOpen] = useState(false)
  const [inboxTab, setInboxTab] = useState<'notifications' | 'tasks'>('notifications')

  // Follow navigation that happens outside the bar (e.g. a notification link).
  useEffect(() => {
    if (routeSection) setSection(routeSection)
  }, [routeSection])

  // Close the inbox on outside tap
  useEffect(() => {
    if (!isInboxOpen) return
    const handleClick = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setIsInboxOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isInboxOpen])

  const current = groups.find((group) => group.key === section) ?? groups[0]
  const tasksLabel = activeCount > 0 ? `${t('sidebar.tasks')} (${activeCount})` : t('sidebar.tasks')

  return (
    <div
      ref={barRef}
      data-testid="mobile-bottom-bar"
      className="fixed bottom-0 z-nav w-full border-t border-border bg-surface sm:hidden"
    >
      {isInboxOpen && (
        <div
          data-testid="mobile-inbox-sheet"
          className="absolute inset-x-0 bottom-full max-h-[70vh] overflow-y-auto rounded-t-2xl border-t border-border bg-surface p-3 shadow-lg"
        >
          <SegmentedControl<'notifications' | 'tasks'>
            aria-label={t('sidebar.inbox')}
            value={inboxTab}
            onChange={setInboxTab}
            className="mb-3 w-full"
            optionClassName="flex-1"
            options={[
              {
                value: 'notifications',
                label: t('notifications.title'),
                'data-testid': 'mobile-inbox-notifications',
              },
              { value: 'tasks', label: tasksLabel, 'data-testid': 'mobile-inbox-tasks' },
            ]}
          />
          {inboxTab === 'notifications' ? (
            <NotificationList onClose={() => setIsInboxOpen(false)} />
          ) : (
            <ActiveTasksList />
          )}
        </div>
      )}

      <nav className="flex h-16 flex-row">
        {isSwitching ? (
          <div className="flex min-w-0 flex-1" data-testid="mobile-group-list">
            {groups.map((group) => (
              <Button
                key={group.key}
                variant="unstyled"
                data-testid={`mobile-group-${group.key}`}
                onClick={() => {
                  setSection(group.key)
                  setIsSwitching(false)
                }}
                className={cn([
                  'flex flex-1 flex-col items-center justify-center p-2 text-xs transition-colors',
                  group.key === current.key
                    ? 'bg-primary-100 text-primary'
                    : 'text-gray-600 hover:bg-surface-hover hover:text-gray-900',
                ])}
              >
                <MobileTabContent icon={group.icon} label={group.label} />
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex min-w-0 flex-1">
            <Button
              variant="unstyled"
              data-testid="mobile-group-switcher"
              aria-label={t('sidebar.switchMenu', { current: current.label })}
              onClick={() => setIsSwitching(true)}
              className="flex w-14 shrink-0 items-center justify-center border-r border-border bg-primary-100 p-2 text-xs font-semibold text-primary"
            >
              <MobileTabContent icon={current.icon} label={current.label} switcher />
            </Button>
            <div className="flex min-w-0 flex-1 overflow-x-auto [scrollbar-width:none]">
              {current.items.map((item) => (
                <MobileChildLink key={item.to} {...item} />
              ))}
              {current.key === 'settings' && (
                <>
                  <div className="flex shrink-0 items-center px-2">
                    <LanguageSwitcher />
                  </div>
                  <Button
                    variant="text"
                    tone="error"
                    iconOnly
                    icon={<FaSignOutAlt className="h-5 w-5" aria-hidden="true" />}
                    tooltip={t('sidebar.logout')}
                    onClick={onLogout}
                    className="shrink-0 px-3"
                  />
                </>
              )}
            </div>
          </div>
        )}

        <Button
          variant="unstyled"
          data-testid="mobile-inbox"
          aria-expanded={isInboxOpen}
          tooltip={isInboxOpen ? undefined : t('sidebar.inbox')}
          onClick={() => setIsInboxOpen((prev) => !prev)}
          className={cn([
            'flex w-14 shrink-0 items-center justify-center border-l border-border p-2 text-xs transition-colors',
            isInboxOpen
              ? 'bg-primary-100 text-primary'
              : 'text-gray-600 hover:bg-surface-hover hover:text-gray-900',
          ])}
        >
          <MobileFlyoutButtonContent
            icon={<BellIcon className="h-5 w-5" aria-hidden="true" />}
            label={t('sidebar.inbox')}
            count={unreadCount}
            badgeAriaLabel={t('notifications.unreadCount', { count: unreadCount })}
            showFailureIndicator={failureCount > 0}
            failureIndicatorAriaLabel={t('asyncJobs.failedIndicator', { count: failureCount })}
            badgeTestId="mobile-inbox-badge"
            failureTestId="mobile-inbox-failed"
          />
        </Button>
      </nav>
    </div>
  )
}

// Icon + label for a bar tab. The pinned switcher shows the icon only (label for
// screen readers) with an up/down chevron so it reads as "tap to change".
const MobileTabContent = ({
  icon,
  label,
  switcher,
}: {
  icon: ReactNode
  label: string
  switcher?: boolean
}) => (
  <>
    {icon}
    {switcher ? (
      <>
        <span className="sr-only">{label}</span>
        <ChevronUpDownIcon className="h-3 w-3" aria-hidden="true" />
      </>
    ) : (
      <span className="mt-1">{label}</span>
    )}
  </>
)

// --- Desktop sidebar ---

const DesktopSidebar = ({
  isCollapsed,
  toggleCollapsed,
  onLogout,
}: {
  isCollapsed: boolean
  toggleCollapsed: () => void
  onLogout: () => void
}) => {
  const { t } = useTranslation()
  const { hasPermissionInAnyOrg } = usePermissions()

  const hasExpenses = hasPermissionInAnyOrg('expenses.access')
  const hasReservations = hasPermissionInAnyOrg('reservations.access')
  const hasWhatsapp =
    hasPermissionInAnyOrg('chat.access') ||
    hasPermissionInAnyOrg(WHATSAPP_TEMPLATES_MANAGE_PERMISSION)
  const hasProperties = hasPermissionInAnyOrg('properties.access')
  const hasReports = hasPermissionInAnyOrg('reports.access')
  const hasReceipts = hasPermissionInAnyOrg('receipts.access')
  const hasStatements = hasPermissionInAnyOrg('statements.access')
  const hasVendors = hasPermissionInAnyOrg('vendors.access')

  const hasAnyAccounting = hasExpenses || hasReports || hasReceipts || hasStatements || hasVendors

  return (
    <div
      className={`hidden border-r border-border bg-surface transition-all duration-200 sm:relative sm:flex sm:h-auto sm:flex-col sm:justify-between sm:p-4 ${
        isCollapsed ? 'sm:w-20' : 'sm:w-64'
      }`}
    >
      {/* Desktop toggle button */}
      <Button
        variant="secondary"
        iconOnly
        shape="circle"
        size="sm"
        onClick={toggleCollapsed}
        className="absolute -right-3 top-4 shadow-md"
        tooltip={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
      >
        {isCollapsed ? (
          <ChevronRightIcon className="h-3 w-3" />
        ) : (
          <ChevronLeftIcon className="h-3 w-3" />
        )}
      </Button>

      <div className="flex flex-col space-y-2">
        {/* Hosting group — shown when any hosting module is available */}
        {(hasReservations || hasProperties || hasWhatsapp) && (
          <SidebarGroup label={t('sidebar.hosting')} isCollapsed={isCollapsed}>
            {hasReservations && (
              <SidebarLink
                to="/reservations"
                isCollapsed={isCollapsed}
                title={t('sidebar.reservations')}
              >
                <FaHome className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.reservations')}
                </span>
              </SidebarLink>
            )}

            {hasProperties && (
              <SidebarLink
                to="/properties"
                isCollapsed={isCollapsed}
                title={t('sidebar.properties')}
              >
                <FaBuilding className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.properties')}
                </span>
              </SidebarLink>
            )}

            {hasWhatsapp && (
              <SidebarLink
                to="/chat-clients"
                isCollapsed={isCollapsed}
                title={t('sidebar.whatsapp')}
              >
                <FaComments className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.whatsapp')}
                </span>
              </SidebarLink>
            )}
          </SidebarGroup>
        )}

        {/* Accounting group — shown when any accounting module is available */}
        {hasAnyAccounting && (
          <SidebarGroup label={t('sidebar.accounting')} isCollapsed={isCollapsed}>
            {hasExpenses && (
              <SidebarLink to="/expenses" isCollapsed={isCollapsed} title={t('sidebar.expenses')}>
                <FaDollarSign className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.expenses')}
                </span>
              </SidebarLink>
            )}

            {hasReports && (
              <SidebarLink
                to="/expense-reports"
                isCollapsed={isCollapsed}
                title={t('sidebar.reports')}
              >
                <FaFileAlt className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.reports')}
                </span>
              </SidebarLink>
            )}

            {hasReceipts && (
              <SidebarLink to="/receipts" isCollapsed={isCollapsed} title={t('sidebar.receipts')}>
                <FaReceipt className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.receipts')}
                </span>
              </SidebarLink>
            )}

            {hasExpenses && (
              <SidebarLink
                to="/payment-accounts"
                isCollapsed={isCollapsed}
                title={t('sidebar.paymentAccounts')}
              >
                <FaWallet className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.paymentAccounts')}
                </span>
              </SidebarLink>
            )}

            {hasStatements && (
              <SidebarLink
                to="/statements"
                isCollapsed={isCollapsed}
                title={t('sidebar.statements')}
              >
                <FaFileInvoiceDollar className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.statements')}
                </span>
              </SidebarLink>
            )}

            {hasVendors && (
              <SidebarLink to="/vendors" isCollapsed={isCollapsed} title={t('sidebar.vendors')}>
                <FaUsers className="h-6 w-6" />
                <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
                  {t('sidebar.vendors')}
                </span>
              </SidebarLink>
            )}
          </SidebarGroup>
        )}

        {/* Billing now lives inside Settings — no standalone nav entry. */}

        {/* Notifications — flyout entry badging the unread count */}
        <NotificationsNavEntry isCollapsed={isCollapsed} />

        {/* Active/failed background-task alerts live in the floating
            ActiveTasksWidget pill, not the side nav. */}

        {/* Settings */}
        <SidebarLink
          to="/organization-settings"
          isCollapsed={isCollapsed}
          title={t('sidebar.settings')}
        >
          <FaCog className="h-6 w-6" />
          <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>
            {t('sidebar.settings')}
          </span>
        </SidebarLink>
      </div>

      <div className="flex flex-col items-center space-y-2">
        <LanguageSwitcher isCollapsed={isCollapsed} />
        <SidebarButton onClick={onLogout} isCollapsed={isCollapsed} title={t('sidebar.logout')}>
          <FaSignOutAlt className="h-6 w-6" />
          <span className={isCollapsed ? 'hidden' : 'hidden sm:block'}>{t('sidebar.logout')}</span>
        </SidebarButton>
      </div>
    </div>
  )
}

// --- Main export ---

export const Sidebar = () => {
  const token = useAuth.use.token()
  const isAuth = Boolean(token)
  const { mutate: logout } = useLogout()
  const navigate = useNavigate()
  const { isCollapsed, toggleCollapsed } = useSidebar()
  // Keep the persisted user (and its memberships, which gate nav items) fresh.
  // Without this a stale cached user hides permission-gated entries until the
  // user happens to visit a page that refetches it.
  useUser()

  const logOutUser = () => {
    logout(undefined, {
      onSettled: () => {
        navigate('log-in')
      },
    })
  }

  if (!isAuth) return null

  return (
    <>
      <MobileBottomBar onLogout={logOutUser} />
      <DesktopSidebar
        isCollapsed={isCollapsed}
        toggleCollapsed={toggleCollapsed}
        onLogout={logOutUser}
      />
    </>
  )
}
